import { TELEPHONE_NUMBER_REGEX } from '@cibo/core'
import { Loading } from '@cibo/profile'
import {
  DEFAULT_MAILING_ADDRESS,
  UsdaMailingAddressField,
} from '@cibo/profile/src/features/ResourceDetailFeatures/EQIP/UsdaGrowerDetails/UsdaMailingAddressField'
import { ResourceDetailFeatureTaskEditorProps, SubmitButton } from '@cibo/ui'
import CheckCircle from '@mui/icons-material/CheckCircle'
import { Button, FormLabel, Grid2 as Grid, Stack, Typography } from '@mui/material'
import { Field, Form, Formik, useFormikContext } from 'formik'
import { TextField } from 'formik-mui'
import { mergeAll } from 'ramda'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import * as yup from 'yup'
import { useProgramEngagementDetails, useUpdateProgramEngagementDetails } from '../../../../queries'
import { EntityContactInformation, EntityContactInformationDetail } from './types'

type EntityContactFormValues = EntityContactInformation

const detailFromFormValues = (values: EntityContactFormValues) => {
  return {
    traitId: 'eqipEntity' as const,
    value: values,
  }
}

export const EntityContactInformationTaskEditorForm = ({
  engagementId,
  detailRequirements,
  onClear,
}: EntityContactInformationTaskEditorProps & { onClear(): void }) => {
  const { t } = useTranslation('@cibo/programs/EntityContactInformationTaskEditor')

  const details = useProgramEngagementDetails({ resourceId: engagementId, detailRequirements })

  const detail: EntityContactInformationDetail = details
    ?.data?.[0] as EntityContactInformationDetail

  const disabled = detail?.immutable

  const { dirty } = useFormikContext<EntityContactFormValues>()

  return (
    <Form>
      <Stack spacing={2}>
        <Stack>
          <Typography>{t('aboutEntity')}</Typography>
          <Typography variant="caption">{t('cta')}</Typography>
        </Stack>

        <Stack spacing={1}>
          <FormLabel required>{t('legalName')}</FormLabel>
          <Field
            id="legalName"
            name="legalName"
            component={TextField}
            InputProps={{
              inputProps: {
                'data-testid': 'legalName',
                autoComplete: 'name',
              },
            }}
            maxLength={255}
            placeholder={t('enterLegalName')}
            disabled={disabled}
            fullWidth={true}
          />
        </Stack>

        <Stack spacing={1}>
          <FormLabel required>{t('phoneHelperText')}</FormLabel>
          <Grid container columns={2} rowSpacing={2}>
            <Grid size={{ xs: 1 }}>
              <Stack spacing={1} mr={2}>
                <FormLabel>{t('mobilePhoneNumber')}</FormLabel>
                <Field
                  id="mobilePhone"
                  name="mobilePhone"
                  component={TextField}
                  InputProps={{
                    inputProps: {
                      'data-testid': 'mobilePhone',
                      autoComplete: 'tel',
                    },
                  }}
                  maxLength={255}
                  placeholder={t('enterMobilePhoneNumber')}
                  disabled={disabled}
                  fullWidth={true}
                />
              </Stack>
            </Grid>
            <Grid size={{ xs: 1 }}>
              <Stack spacing={1} mr={2}>
                <FormLabel>{t('businessPhoneNumber')}</FormLabel>
                <Field
                  id="businessPhone"
                  name="businessPhone"
                  component={TextField}
                  InputProps={{
                    inputProps: {
                      'data-testid': 'businessPhone',
                      autoComplete: 'tel',
                    },
                  }}
                  maxLength={255}
                  placeholder={t('enterBusinessPhoneNumber')}
                  disabled={disabled}
                  fullWidth={true}
                />
              </Stack>
            </Grid>
          </Grid>
        </Stack>

        <Stack spacing={1}>
          <Typography>{t('entityMailingAddress')}</Typography>

          <UsdaMailingAddressField name="mailingAddress" disabled={disabled} />
        </Stack>

        <Stack direction="row" spacing={2} padding={2} justifyContent="flex-end">
          <Button
            variant="outlined"
            onClick={() => onClear()}
            disabled={disabled || !detail?.value}
          >
            {t('clear')}
          </Button>
          <SubmitButton
            disabled={disabled || !dirty}
            data-testid="save-button"
            endIcon={detail?.value && !dirty ? <CheckCircle fontSize="small" /> : undefined}
          >
            {detail?.value ? (!dirty ? t('saved') : t('update')) : t('save')}
          </SubmitButton>
        </Stack>
      </Stack>
    </Form>
  )
}

const DEFAULT_VALUES = {
  legalName: '',
  mobilePhone: '',
  businessPhone: '',
  mailingAddress: DEFAULT_MAILING_ADDRESS,
}

type EntityContactInformationTaskEditorProps = Omit<
  ResourceDetailFeatureTaskEditorProps<EntityContactInformationDetail>,
  'engagement' | 'resourceIds' | 'ownerName' | 'userRole' | 'task'
>

export const EntityContactInformationTaskEditor = (
  props: EntityContactInformationTaskEditorProps
) => {
  const { t } = useTranslation('@cibo/programs/EntityContactInformationTaskEditor')
  const { detailRequirements, engagementId, onUpdating, onSuccess, onError } = props
  // @ts-ignore not sure how to tell our types this detail uses value: {...}
  const updateResourceDetail = useUpdateProgramEngagementDetails<EntityContactInformationDetail>()

  // @ts-ignore not sure how to tell our types this detail uses value: {...}
  const details = useProgramEngagementDetails<EntityContactInformationDetail>({
    resourceId: engagementId,
    detailRequirements,
  })
  const detail = details?.data?.[0]

  const schema = useMemo(
    () =>
      yup
        .object({
          legalName: yup.string().required(t('legalNameRequired')),
          businessPhone: yup.string().matches(TELEPHONE_NUMBER_REGEX, t('needsValidPhoneNumber')),
          mobilePhone: yup.string().matches(TELEPHONE_NUMBER_REGEX, t('needsValidPhoneNumber')),
        })
        .test('phoneNumber', t('phoneRequired'), function(value) {
          const phoneNumberPresent = !!value?.businessPhone || !!value?.mobilePhone

          if (!phoneNumberPresent) {
            return this.createError({
              path: 'mobilePhone',
              message: t('phoneRequired'),
            })
          }

          return true
        }),

    [t]
  )

  const onSubmit = useCallback(
    (values: any, { setSubmitting }: any) => {
      const newDetail = detailFromFormValues(values)

      onUpdating?.()
      updateResourceDetail
        .mutateAsync({
          resourceId: engagementId,
          details: [newDetail],
        })
        .then(onSuccess)
        .catch(onError)
        .finally(() => setSubmitting(false))
    },
    [onUpdating, updateResourceDetail, engagementId, onSuccess, onError]
  )

  const onClear = useCallback(() => {
    const newDetail = { traitId: 'eqipEntity' as const }

    onUpdating?.()
    updateResourceDetail
      .mutateAsync({
        resourceId: engagementId,
        details: [newDetail],
      })
      .then(() => {
        onSuccess?.()
        details.refetch()
      })
      .catch(onError)
  }, [onSuccess, onUpdating, updateResourceDetail, engagementId, onError])

  if (details.isPending) return <Loading />

  const initialValues = mergeAll([DEFAULT_VALUES, detail?.value || {}])

  return (
    <Formik<EntityContactFormValues>
      initialValues={initialValues as EntityContactFormValues}
      onSubmit={onSubmit}
      validationSchema={schema}
      validateOnMount
      enableReinitialize
    >
      <EntityContactInformationTaskEditorForm {...props} onClear={onClear} />
    </Formik>
  )
}
